'use client';
import * as React from 'react';
import { useStore } from '@base-ui/utils/store';
import { useRenderElement } from '@base-ui/react/internals/useRenderElement';
import type { BaseUIComponentProps } from '@base-ui/react/internals/types';
import { useCompositeListItem } from '@base-ui/react/internals/composite';
import { useEventCalendarStoreContext } from '../../use-event-calendar-store-context';
import { useAdapterContext } from '../../use-adapter-context';
import { schedulerNowSelectors } from '../../scheduler-selectors';
import { EVENT_CREATION_PRECISION_MINUTE } from '../../constants';
import { useEventCreation } from '../../internals/utils/useEventCreation';
import { useKeyboardEventCreation } from '../../internals/utils/useKeyboardEventCreation';
import { getNavigationTarget } from '../../internals/utils/getNavigationTarget';
import { useCalendarGridCellsRefsContext } from '../../internals/utils/CalendarGridCellsRefsContext';
import { useCalendarGridRootContext } from '../root/CalendarGridRootContext';
import { CalendarGridTimeColumnContext } from './CalendarGridTimeColumnContext';
import { useTimeDropTarget } from './useTimeDropTarget';

export const CalendarGridTimeColumn = React.forwardRef(function CalendarGridTimeColumn(
  componentProps: CalendarGridTimeColumn.Props,
  forwardedRef: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    // Rendering props
    className,
    render,
    style,
    // Internal props
    start,
    end,
    dayStartMinute = 0,
    dayEndMinute = 24 * 60,
    addPropertiesToDroppedEvent,
    // Props forwarded to the DOM element
    ...elementProps
  } = componentProps;

  const adapter = useAdapterContext();
  const store = useEventCalendarStoreContext();
  const { focusedCell, setFocusedCell, rowTypes, rowsPerType } = useCalendarGridRootContext();
  const isCurrentDay = useStore(store, schedulerNowSelectors.isCurrentDay, start);
  const { ref: listItemRef, index } = useCompositeListItem();
  const cellsRefs = useCalendarGridCellsRefsContext();

  const cellRef = React.useRef<HTMLDivElement>(null);
  const hasFocus =
    focusedCell?.rowType === 'time-grid' &&
    focusedCell?.rowIndex === 0 &&
    focusedCell?.columnIndex === index;

  const {
    getCursorPositionInElementMs,
    getDateAtPointer,
    ref: dropTargetRef,
  } = useTimeDropTarget({
    start,
    end,
    addPropertiesToDroppedEvent,
  });

  const eventCreationProps = useEventCreation(({ event, creationConfig }) => {
    const offsetMs = getCursorPositionInElementMs({
      input: { clientY: event.clientY },
      elementRef: dropTargetRef,
    });
    const anchor = adapter.addMilliseconds(start, offsetMs);
    const startDate = adapter.addMinutes(
      anchor,
      -(adapter.getMinutes(anchor) % EVENT_CREATION_PRECISION_MINUTE),
    );
    return {
      surfaceType: 'time-grid' as const,
      start: startDate,
      end: adapter.addMinutes(startDate, creationConfig.duration),
      resourceId: null,
    };
  });

  const triggerKeyboardCreation = useKeyboardEventCreation(({ creationConfig }) => {
    const noon = adapter.setHours(adapter.setMinutes(start, 0), 12);
    // Keep the default creation anchor inside the visible window when the view limits its hour range.
    const anchor = adapter.isBefore(noon, start) || !adapter.isBefore(noon, end) ? start : noon;
    return {
      surfaceType: 'time-grid' as const,
      start: anchor,
      end: adapter.addMinutes(anchor, creationConfig.duration),
      resourceId: null,
    };
  });

  // Apply DOM focus when this cell becomes the focused cell
  React.useEffect(() => {
    if (hasFocus && cellRef.current && !cellRef.current.contains(document.activeElement)) {
      cellRef.current.focus({ preventScroll: true });
    }
  }, [hasFocus]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    const target = getNavigationTarget(event.key, 'time-grid', 0, index, {
      columnCount: cellsRefs.current.length,
      rowTypes,
      rowsPerType,
    });
    if (target) {
      event.preventDefault();
      setFocusedCell(target);
      return;
    }

    if (event.key === 'Enter' && event.target === event.currentTarget && triggerKeyboardCreation) {
      event.preventDefault();
      triggerKeyboardCreation();
    }
  };

  const handleFocus = (event: React.FocusEvent<HTMLDivElement>) => {
    if (event.target === event.currentTarget) {
      setFocusedCell({ rowType: 'time-grid', rowIndex: 0, columnIndex: index });
    }
  };

  const state: CalendarGridTimeColumn.State = React.useMemo(
    () => ({
      current: isCurrentDay,
    }),
    [isCurrentDay],
  );

  const contextValue: CalendarGridTimeColumnContext = React.useMemo(
    () => ({
      start,
      end,
      dayStartMinute,
      dayEndMinute,
      index,
      hasFocus,
      getCursorPositionInElementMs,
      getDateAtPointer,
    }),
    [
      start,
      end,
      dayStartMinute,
      dayEndMinute,
      index,
      hasFocus,
      getCursorPositionInElementMs,
      getDateAtPointer,
    ],
  );

  const keyboardProps = {
    tabIndex: 0,
    onKeyDown: handleKeyDown,
    onFocus: handleFocus,
  };

  const element = useRenderElement('div', componentProps, {
    state,
    ref: [forwardedRef, dropTargetRef, listItemRef, cellRef],
    props: [
      { role: 'gridcell', 'aria-colindex': index + 1 },
      keyboardProps,
      eventCreationProps,
      elementProps,
    ],
  });

  return (
    <CalendarGridTimeColumnContext.Provider value={contextValue}>
      {element}
    </CalendarGridTimeColumnContext.Provider>
  );
});

export namespace CalendarGridTimeColumn {
  export interface State {
    /**
     * Whether the column represents the current day.
     */
    current: boolean;
  }

  export interface Props extends BaseUIComponentProps<'div', State>, useTimeDropTarget.Parameters {
    /**
     * First displayed minute of the day, as an offset from midnight.
     * Derived from the view's whole-hour window so it stays aligned with the
     * grid rows even on DST-transition days.
     * @default 0
     */
    dayStartMinute?: number;
    /**
     * End of the displayed window of the day, as an exclusive offset from midnight (1440 for the full day).
     * Derived from the view's whole-hour window so it stays aligned with the
     * grid rows even on DST-transition days.
     * @default 1440
     */
    dayEndMinute?: number;
  }
}
